import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heartbeat, Moon, Lightning, Barbell, Warning } from "@phosphor-icons/react";

const DEVICES = [
  { key: "whoop", name: "Whoop", color: "#000000" },
  { key: "garmin", name: "Garmin", color: "#007CC3" },
  { key: "apple_health", name: "Apple Health", color: "#FF3B30" },
  { key: "oura", name: "Oura", color: "#5AC8FA" },
];

export default function Readiness() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    sleep_hours: "",
    energy_1_10: "",
    soreness_1_10: "",
    hrv_ms: "",
    resting_hr: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [t, h] = await Promise.all([
      api.get("/readiness/today").catch(() => ({ data: null })),
      api.get("/readiness/history?days=14").catch(() => ({ data: [] })),
    ]);
    setToday(t.data);
    setHistory(h.data || []);
    if (t.data) setForm({ ...form, ...t.data });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        sleep_hours: form.sleep_hours ? Number(form.sleep_hours) : null,
        energy_1_10: form.energy_1_10 ? Number(form.energy_1_10) : null,
        soreness_1_10: form.soreness_1_10 ? Number(form.soreness_1_10) : null,
        hrv_ms: form.hrv_ms ? Number(form.hrv_ms) : null,
        resting_hr: form.resting_hr ? Number(form.resting_hr) : null,
        source: "manual",
      };
      const { data } = await api.post("/readiness", payload);
      setToday(data);
      load();
      toast.success(`Readiness logged — score ${data.score}/100`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const score = today?.score || 0;
  const scoreColor = score >= 80 ? "#34C759" : score >= 55 ? "#FF9F0A" : score > 0 ? "#FF3B30" : "#A3A3A3";
  const scoreLabel = score >= 80 ? "Primed" : score >= 55 ? "Ready" : score > 0 ? "Under-Recovered" : "Not Logged";

  return (
    <Layout>
      <div className="p-8 md:p-12 max-w-5xl">
        <div className="mb-8">
          <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Wearable Sync</div>
          <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
            <Heartbeat size={48} weight="duotone" className="text-[#FF3B30]" /> Readiness
          </h1>
          <p className="text-[#A3A3A3] mt-2">Log daily. Your AI plan auto-adjusts when you're cooked.</p>
        </div>

        {/* Score */}
        <div className="border p-8 mb-6 flex items-center justify-between flex-wrap gap-6"
          style={{ borderColor: scoreColor, backgroundColor: `${scoreColor}12` }} data-testid="readiness-score">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">Today's Score</div>
            <div className="font-heading font-black text-7xl mt-1" style={{ color: scoreColor }}>{score || "—"}</div>
            <div className="font-heading uppercase tracking-widest text-sm mt-1" style={{ color: scoreColor }}>{scoreLabel}</div>
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">Sleep</div>
              <div className="font-heading font-bold text-2xl">{today?.sleep_hours ?? "—"}<span className="text-sm text-[#A3A3A3]">h</span></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">Energy</div>
              <div className="font-heading font-bold text-2xl">{today?.energy_1_10 ?? "—"}<span className="text-sm text-[#A3A3A3]">/10</span></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">Soreness</div>
              <div className="font-heading font-bold text-2xl">{today?.soreness_1_10 ?? "—"}<span className="text-sm text-[#A3A3A3]">/10</span></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">HRV</div>
              <div className="font-heading font-bold text-2xl">{today?.hrv_ms ?? "—"}<span className="text-sm text-[#A3A3A3]">ms</span></div>
            </div>
          </div>
        </div>

        {/* Device sync (mocked) */}
        <div className="border border-[#262626] bg-[#121212] p-6 mb-6" data-testid="device-sync">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-bold uppercase text-lg accent-underline inline-block">Connect Device</h2>
              <p className="text-xs text-[#A3A3A3] mt-2">Auto-sync coming soon. For now, log manually below.</p>
            </div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF9F0A] border border-[#FF9F0A] px-2 py-1 flex items-center gap-1">
              <Warning size={14} /> Waitlist
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DEVICES.map((d) => (
              <button key={d.key} onClick={() => toast.info(`${d.name} sync — added to waitlist. Roll out Q2.`)}
                data-testid={`device-${d.key}`}
                className="border border-[#262626] bg-[#0A0A0A] hover:border-[#FF3B30] p-4 text-left transition-colors">
                <div className="font-heading font-bold uppercase text-sm">{d.name}</div>
                <div className="text-xs text-[#A3A3A3] mt-1">Notify me</div>
              </button>
            ))}
          </div>
        </div>

        {/* Manual form */}
        <form onSubmit={submit} className="border border-[#262626] bg-[#121212] p-6 mb-6">
          <h2 className="font-heading font-bold uppercase text-lg mb-4 accent-underline inline-block">Log Today</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="readiness-date" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Sleep (hours)</Label>
              <Input type="number" step="0.1" min="0" max="16" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })}
                data-testid="readiness-sleep" placeholder="7.5" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Energy (1-10)</Label>
              <Input type="number" min="1" max="10" value={form.energy_1_10} onChange={(e) => setForm({ ...form, energy_1_10: e.target.value })}
                data-testid="readiness-energy" placeholder="8" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Soreness (1-10)</Label>
              <Input type="number" min="1" max="10" value={form.soreness_1_10} onChange={(e) => setForm({ ...form, soreness_1_10: e.target.value })}
                data-testid="readiness-soreness" placeholder="3" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">HRV (ms) - optional</Label>
              <Input type="number" min="0" value={form.hrv_ms} onChange={(e) => setForm({ ...form, hrv_ms: e.target.value })}
                data-testid="readiness-hrv" placeholder="55" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Resting HR - optional</Label>
              <Input type="number" min="0" value={form.resting_hr} onChange={(e) => setForm({ ...form, resting_hr: e.target.value })}
                data-testid="readiness-rhr" placeholder="52" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="readiness-submit"
            className="mt-4 rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6">
            {saving ? "Saving…" : "Save Readiness"}
          </Button>
        </form>

        {/* History */}
        {history.length > 0 && (
          <div className="border border-[#262626] bg-[#121212]">
            <div className="p-4 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">Last 14 Days</div>
            <div className="p-4">
              <div className="flex items-end gap-1 h-32">
                {history.slice().reverse().map((r, i) => {
                  const h = Math.max(4, (r.score || 0));
                  const color = r.score >= 80 ? "#34C759" : r.score >= 55 ? "#FF9F0A" : "#FF3B30";
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${r.date}: ${r.score}`}>
                      <div style={{ height: `${h}%`, backgroundColor: color, width: "100%" }} data-testid={`bar-${r.date}`} />
                    </div>
                  );
                })}
              </div>
              <div className="text-xs text-[#A3A3A3] text-center mt-2">Daily readiness score</div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
